import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MenuIcon,
  XIcon,
  LoginIcon,
  LogoutIcon,
  UserIcon,
  ChevronDownIcon,
  BellIcon,
} from "@heroicons/react/outline";
import { getAuth, signOut } from "firebase/auth";
import { useAuth } from "../../../Contexts/AuthContext";
import { useNotifications } from "../../../hooks/useNotifications";

const EventsTopBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const { currentUser, userData } = useAuth();
  const { notifications, unreadCount, markAllAsRead } = useNotifications();
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleNotifications = async () => {
    const wasOpen = isNotificationOpen;
    setIsNotificationOpen(!isNotificationOpen);

    // If opening the notifications and there are unread notifications, mark all as read
    if (!wasOpen && unreadCount > 0) {
      await markAllAsRead();
    }
  };

  const handleNotificationClick = async (notification) => {
    setIsNotificationOpen(false);

    // Handle navigation based on notification type
    if (notification.type === "team_invitation") {
      navigate(
        `/hackathon-dashboard?eventId=${notification.relatedData?.eventId}`,
      );
    }
  };
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Navigation links
  const navLinks = [
    { path: "/", label: "Events" },
    ...(currentUser ? [{ path: "/my-events", label: "Dashboard" }] : []),
  ];

  const CustomNavLink = ({ to, children }) => {
    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          isActive
            ? "text-custom-green-1 border-b-2 border-custom-green-1 font-medium"
            : "text-white hover:text-custom-green-1 transition duration-200"
        }
        end
      >
        {children}
      </NavLink>
    );
  };

  return (
    <nav className="bg-custom-blue-1 shadow-lg fixed w-full top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and site name */}
          <div className="flex items-center gap-10">
            <NavLink to="/" className="flex items-center space-x-3">
              <img
                src="/Images/STIlogo.svg"
                alt="STI Logo"
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-white font-bold text-lg">STI</span>
                <span className="text-custom-green-1 text-xs -mt-1">
                  Events
                </span>
              </div>
            </NavLink>
            {navLinks.map((link) => (
              <CustomNavLink key={link.path} to={link.path}>
                {link.label}
              </CustomNavLink>
            ))}
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Conditional rendering based on authentication state */}
            {currentUser ? (
              <div className="flex items-center space-x-4">
                {/* Notifications */}
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={toggleNotifications}
                    className="relative text-white hover:text-custom-green-1 transition-colors focus:outline-none"
                  >
                    <BellIcon className="h-6 w-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications dropdown */}
                  {isNotificationOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-200 max-h-96 overflow-y-auto">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <h3 className="text-sm font-medium text-gray-700">
                          Notifications
                        </h3>
                      </div>

                      {notifications.length === 0 ? (
                        <div className="px-4 py-6 text-center text-gray-500 text-sm">
                          No notifications yet
                        </div>
                      ) : (
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.slice(0, 10).map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 bg-custom-blue-1/5"
                            >
                              <div className="flex items-start space-x-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 leading-relaxed">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {notification.timestamp?.toDate
                                      ? notification.timestamp
                                          .toDate()
                                          .toLocaleDateString() +
                                        " " +
                                        notification.timestamp
                                          .toDate()
                                          .toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                      : "Just now"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* User dropdown */}
                <div className="relative" ref={dropdownRef}>
                  {/* User dropdown trigger */}
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center space-x-2 text-white hover:text-custom-green-1 transition-colors focus:outline-none"
                  >
                    <UserIcon className="h-5 w-5" />
                    <span className="text-sm">
                      {userData?.fullName || currentUser.email?.split("@")[0]}
                    </span>
                    <ChevronDownIcon
                      className={`h-4 w-4 transition-transform ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Dropdown menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-700">
                          {userData?.fullName || "User"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {currentUser.email}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                      >
                        <LogoutIcon className="h-4 w-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="bg-custom-green-1 text-custom-blue-1 px-4 py-2 rounded-md font-medium flex items-center hover:bg-custom-green-1/90 transition-colors"
              >
                <LoginIcon className="h-5 w-5 mr-2" />
                Login
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="text-white hover:text-custom-green-1 focus:outline-none"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={toggleMenu}
          ></div>
          <div className="fixed top-0 right-0 w-[75%] max-w-sm h-full bg-custom-blue-1 shadow-lg z-20 transform transition-all duration-300">
            <div className="p-5">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-3">
                  <img
                    src="/Images/STIlogo.svg"
                    alt="STI Logo"
                    className="h-8 w-auto"
                  />
                  <span className="text-white font-bold">STI Events</span>
                </div>
                <button onClick={toggleMenu} className="text-white">
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-col space-y-6">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `text-lg ${
                        isActive
                          ? "text-custom-green-1"
                          : "text-white hover:text-gray-300"
                      }`
                    }
                    onClick={toggleMenu}
                  >
                    {link.label}
                  </NavLink>
                ))}

                {/* Conditional rendering for mobile menu */}
                {currentUser ? (
                  <div className="pt-4 border-t border-white/20">
                    {/* User info in mobile */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-6 w-6 text-custom-green-1" />
                        <span className="text-white">
                          {userData?.fullName ||
                            currentUser.email?.split("@")[0]}
                        </span>
                      </div>

                      {/* Mobile Notifications */}
                      <div className="relative">
                        <BellIcon className="h-6 w-6 text-white" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                            {unreadCount > 9 ? "9+" : unreadCount}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Mobile notifications list */}
                    {notifications.length > 0 && (
                      <div className="mb-4 max-h-32 overflow-y-auto bg-white/10 rounded-lg p-2">
                        <div className="text-xs text-white/80 mb-2">
                          Recent Notifications
                        </div>
                        {notifications.slice(0, 3).map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => {
                              handleNotificationClick(notification);
                              toggleMenu();
                            }}
                            className="text-xs text-white/90 p-2 bg-white/5 rounded mb-1 cursor-pointer hover:bg-white/10"
                          >
                            {notification.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Mobile logout button */}
                    <button
                      onClick={() => {
                        handleLogout();
                        toggleMenu();
                      }}
                      className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md font-medium flex items-center justify-center transition-colors"
                    >
                      <LogoutIcon className="h-5 w-5 mr-2" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      navigate("/login");
                      toggleMenu();
                    }}
                    className="bg-custom-green-1 text-custom-blue-1 px-4 py-2 rounded-md font-medium flex items-center justify-center mt-4 hover:bg-custom-green-1/90 transition-colors"
                  >
                    <LoginIcon className="h-5 w-5 mr-2" />
                    Login
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default EventsTopBar;
